import { Box, Table, Text } from "@chakra-ui/react";
import ReportTitleWithInfo from "./ReportTitleWithInfo";

const DEFAULT_SUMMARY_DESCRIPTION =
  "Highlights the main operating signals for this report before the detailed rows. Use it to identify the biggest volume, time, demand, or exception drivers, then validate the detail table below.";

const getTableDescription = (table) => {
  if (table.description) return table.description;
  const title = table.title || "Summary table";
  if (/demand|arrival/i.test(title)) {
    return "Ranks the highest demand areas and shows peak, zero-demand, active-period, and volatility signals for capacity planning.";
  }
  if (/waiting|service time|duration/i.test(title)) {
    return "Ranks the areas consuming the most time and compares total time, average time per ticket, peaks, zero-work periods, and duration bands.";
  }
  if (/ticket|volume|ranking/i.test(title)) {
    return "Ranks ticket volume and exception signals so high-volume or poor-completion areas are visible before the detailed table.";
  }
  return "Explains the main ranked signals used to prioritize the detailed rows that follow.";
};

const EnterpriseReportSummary = ({
  title = "Enterprise Summary",
  description = DEFAULT_SUMMARY_DESCRIPTION,
  kpis = [],
  tables = [],
  notes = [],
}) => {
  const summaryGridClassName = `enterpriseSummaryGrid${
    tables.length === 1 ? " enterpriseSummaryGrid--single" : ""
  }`;

  return (
  <Box className="enterpriseReportSummary" mb="20px">
    <ReportTitleWithInfo description={description}>{title}</ReportTitleWithInfo>

    {kpis.length ? (
      <Box className="enterpriseKpiGrid">
        {kpis.map((kpi) => (
          <Box key={`${kpi.label}-${kpi.value}`}>
            <Text>{kpi.label}</Text>
            <strong>{kpi.value}</strong>
            {kpi.note ? <span>{kpi.note}</span> : null}
          </Box>
        ))}
      </Box>
    ) : null}

    {tables.length ? (
      <Box className={summaryGridClassName}>
        {tables.map((table) => (
          <Box key={table.title}>
            <ReportTitleWithInfo as="h4" description={getTableDescription(table)}>
              {table.title}
            </ReportTitleWithInfo>
            <Box className="enterpriseSummaryTableWrap">
              <Table.Root className="stripedTable reportDataTable reportDataTable--compact" size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader className="enterpriseSummaryTableIndexCell">
                      <Text as="span" className="reportDataTableHeaderText">#</Text>
                    </Table.ColumnHeader>
                    {table.columns.map((column) => (
                      <Table.ColumnHeader
                        key={column.id}
                        className={`enterpriseSummaryTableCell enterpriseSummaryTableCell--${column.id}`}
                      >
                        <Text as="span" className="reportDataTableHeaderText">{column.label}</Text>
                      </Table.ColumnHeader>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {table.rows.map((row, index) => (
                    <Table.Row key={`${row.scopeName || row.name || "row"}-${index}`}>
                      <Table.Cell className="enterpriseSummaryTableIndexCell">{index + 1}</Table.Cell>
                      {table.columns.map((column) => (
                        <Table.Cell
                          key={column.id}
                          className={`enterpriseSummaryTableCell enterpriseSummaryTableCell--${column.id}`}
                        >
                          {table.renderCell ? table.renderCell(row, column.id) : row[column.id]}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Box>
        ))}
      </Box>
    ) : null}

    {notes.length ? (
      <Text fontSize="sm" color="gray.600" mt="10px">
        {notes.join(" ")}
      </Text>
    ) : null}
  </Box>
  );
};

export default EnterpriseReportSummary;
